import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
    BaseFeatureArgs,
    InitialQueryState,
    QueryManagerOptions,
    SkipOrArgs,
} from '@common/types/queryState';
import { AnyFeatureHook } from '@common/hooks';

const sanitizeArgs = <T extends BaseFeatureArgs>(args: T): T => {
    const {
        offset: _offset,
        limit: _limit,
        page: _page,
        pageIndex: _pageIndex,
        pageSize: _pageSize,
        sort: _sort,
        sorting: _sorting,
        ...cleanArgs
    } = args as Record<string, unknown>;

    return cleanArgs as unknown as T;
};

export const useHeadlessQuery = <TData, TArgs extends BaseFeatureArgs>(
    useFeatureHook: AnyFeatureHook<TData, TArgs>,
    initialArgs: InitialQueryState = {},
    args: SkipOrArgs<TArgs> = skipToken,
    options?: QueryManagerOptions,
) => {
    const headlessArgs = useMemo(() => {
        if (args === skipToken) return skipToken;
        return sanitizeArgs(args);
    }, [args]);

    const headlessOptions = useMemo<QueryManagerOptions>(
        () => ({
            ...options,
            skipUrlSync: true,
            paramsOverride: {
                pageIndex: 0,
                ...initialArgs,
                ...options?.paramsOverride,
            },
        }),
        [options, initialArgs],
    );

    return useFeatureHook(initialArgs, headlessArgs, headlessOptions);
};
